import { createBrowserRouter } from "react-router-dom";
import App from "../App";
import ProtectedApp from "../ProtectedApp";
import { ErrorPage } from "../common-ui/ErrorPage";
import { Users } from "../features/Users/Users";
import { UserInvitations } from "../features/UserInvitations/UserInvitations";
import { UserInvitation } from "../features/UserInvitations/UserInvitation";
import { NewUserInvitation } from "../features/UserInvitations/NewUserInvitation";
import { RetrieveUsername } from "../features/login/RetrieveUsername";
import { UserInstructions } from "../features/UserInvitations/UserInstructions";
import { Register } from "../features/login/Register";
import { UserInvitationsBulkUpload } from "../features/UserInvitations/UserInvitationsBulkUpload";
import { UserInvitationsBulkInvite } from "../features/UserInvitations/UserInvitationsBulkInvite";
import { Programs } from "../features/Programs/Programs";
import { NewProgram } from "../features/Programs/NewProgram";
import { Program } from "../features/Programs/Program";
import { EditProgram } from "../features/Programs/EditProgram";
import { Institutions } from "../features/Institutions/Institutions";
import { Institution } from "../features/Institutions/Institution";
import { NewInstitution } from "../features/Institutions/NewInstitution";
import { EditInstitution } from "../features/Institutions/EditInstitution";
import { AssessmentDefinitions } from "../features/Assessments/AssessmentDefinitions";
import { EditInstrument } from "../features/Instruments/EditInstrument";
import { UserReports } from "../features/Reports/UserReports";
import { UserReport } from "../features/Reports/UserReport";
import { ProgramReports } from "../features/Reports/ProgramReports";
import { Layout } from "../features/nav/Layout";
import { RootRedirect } from "../common-ui/RootRedirect";
import { PendingAssessments } from "../features/Assessments/PendingAssessments";
import { CreateAssessmentEvent } from "../features/Assessments/CreateAssessmentEvent/CreateAssessmentEvent";
import { CompleteAssessmentEvent } from "../features/AssessmentEvents/CompleteAssessmentEvent/CompleteAssessmentEvent";
import { AssessmentDefinition } from "../features/Assessments/AssessmentDefinition";
import { ProtectedRoute } from "../features/nav/ProtectedRoute";
import { CognitoUserGroups } from "../features/Users/userUtils";
import { Redirect } from "../features/Redirection/Redirect";
import { Logout } from "../features/login/Logout";
import { AssessmentRoles } from "../features/Assessments/CreateAssessmentEvent/AssessmentRoles";
import { CompleteRegistration } from "../features/UserInvitations/CompleteRegistration";
import { AssessmentHistoryDebug } from "../features/Debug/AssessmentHistoryDebug";
import { QueryExplorer } from "../features/Debug/QueryExplorer";

export const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/",
        element: <ProtectedApp />,
        children: [
          {
            path: "/",
            element: <Layout />,
            children: [
              {
                path: "/",
                element: <RootRedirect />
              },
              // User-Level Routes (Trainees, Attendings)
              {
                element: (
                  <ProtectedRoute
                    requiredPermissions={[CognitoUserGroups.Users]}
                  />
                ),
                children: [
                  {
                    path: "assessments/pending",
                    element: <PendingAssessments />
                  },
                  {
                    path: "assessments/create",
                    element: <AssessmentRoles />
                  },
                  {
                    path: "assessments/complete/:assessmentEventId",
                    element: <CompleteAssessmentEvent />
                  }
                ]
              },
              // ProgramAdmin-Level Routes
              {
                element: (
                  <ProtectedRoute
                    requiredPermissions={[
                      CognitoUserGroups.ProgramAdmins,
                      CognitoUserGroups.SysAdmins
                    ]}
                  />
                ),
                children: [
                  {
                    path: "users",
                    element: <Users />
                  },
                  {
                    path: "invitations",
                    element: <UserInvitations />
                  },
                  {
                    path: "invitations/:id",
                    element: <UserInvitation />
                  },
                  {
                    path: "invitations/new",
                    element: <NewUserInvitation />
                  },
                  {
                    path: "invitations/bulkupload",
                    element: <UserInvitationsBulkUpload />
                  },
                  {
                    path: "invitations/bulkinvite",
                    element: <UserInvitationsBulkInvite />
                  },
                  {
                    path: "reports/user",
                    element: <UserReports />
                  },
                  {
                    path: "reports/user/classic",
                    element: <UserReports classicMode />
                  },
                  {
                    path: "reports/user/:programMembershipId",
                    element: <UserReport />
                  },
                  {
                    path: "reports/user/:userId/:programId",
                    element: <UserReport />
                  }
                ]
              },
              // SysAdmin-Level Routes
              {
                element: (
                  <ProtectedRoute
                    requiredPermissions={[CognitoUserGroups.SysAdmins]}
                  />
                ),
                children: [
                  {
                    path: "assessmentdefinitions",
                    element: <AssessmentDefinitions />
                  },
                  {
                    path: "programs",
                    element: <Programs />
                  },
                  {
                    path: "programs/new",
                    element: <NewProgram />
                  },
                  {
                    path: "programs/:id",
                    element: <Program />
                  },
                  {
                    path: "programs/:id/edit",
                    element: <EditProgram />
                  },
                  {
                    path: "institutions",
                    element: <Institutions />
                  },
                  {
                    path: "institutions/:id",
                    element: <Institution />
                  },
                  {
                    path: "institutions/new",
                    element: <NewInstitution />
                  },
                  {
                    path: "institutions/:id/edit",
                    element: <EditInstitution />
                  },
                  {
                    path: "assessmentdefinitions",
                    element: <AssessmentDefinitions />
                  },
                  {
                    path: "assessmentdefinitions/:id",
                    element: <AssessmentDefinition />
                  },
                  {
                    path: "instruments/:id/edit",
                    element: <EditInstrument />
                  },
                  {
                    path: "reports/program",
                    element: <ProgramReports />
                  },
                  {
                    path: "reports/program/classic",
                    element: <ProgramReports classicMode />
                  },
                  {
                    path: "debug/assessment-history",
                    element: <AssessmentHistoryDebug />
                  },
                  {
                    path: "debug/queries",
                    element: <QueryExplorer />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: "/logout",
        element: <Logout />
      },
      {
        path: "/retrieveusername",
        element: <RetrieveUsername />
      },
      {
        path: "/v9Jo",
        element: <Redirect />
      },
      {
        path: "/confirmaccountcreation/:id",
        element: <CompleteRegistration />
      },
      {
        path: "/userinstructions",
        element: <UserInstructions />
      },
      {
        path: "/register",
        element: <Register />
      },
      {
        path: "/complete-registration",
        element: <CompleteRegistration />
      }
    ]
  }
]);
